  import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useShopFlag } from "@/hooks/use-flag.ts";
import type { ReactNode } from "react";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right.ts";
import useTranslation from "@babackapp/translations/use-translation";
import {
  BellAlertIcon,
  ShoppingBagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import {
  FileTextIcon,
  LightningBoltIcon,
  PersonIcon,
} from "@radix-ui/react-icons";
import { Fragment } from "react";
import { NavigationSectionItem } from "./section.tsx";
import { NotificationsNavItem } from "./notifications-nav-item.tsx";

export default function SecondaryNavigation() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const hasRight = useViewerHasAtLeastAdminRights();
  const isAlertEnabled = useShopFlag("isAlertEnabled");
  const isRetractionOnly = useIsRetractionOnly();

  if (!shop || !hasRight) return null;

  const navItems: {
    name: string;
    href: string;
    icon: ReactNode;
    badge?: string;
    locked?: boolean;
  }[] = [
    {
      name: t("sidebar.shopInformation"),
      href: `/shops/${shop.id}/settings/shop-informations`,
      icon: <ShoppingBagIcon className="w-4 h-4" />,
    },
    {
      name: t("sidebar.customerPortal"),
      href: `/shops/${shop.id}/settings/customer-portal`,
      icon: <PersonIcon className="w-4 h-4" />,
    },
    {
      name: t("sidebar.carriers"),
      href: `/shops/${shop.id}/settings/carriers`,
      icon: <TruckIcon className="w-4 h-4" />,
      locked: isRetractionOnly,
    },
    {
      name: t("sidebar.integrations"),
      href: `/shops/${shop.id}/settings/integrations`,
      icon: <LightningBoltIcon className="w-4 h-4" />,
      locked: isRetractionOnly,
    },
    ...(isAlertEnabled && !isRetractionOnly
      ? [
          {
            name: t("sidebar.alerts"),
            href: `/shops/${shop.id}/settings/alerts`,
            icon: <BellAlertIcon className="w-4 h-4" />,
          },
        ]
      : []),
  ];

  const isShopifyTestWebsite = shop.email.includes("shopify.com");
  if (!isShopifyTestWebsite) {
    navItems.push({
      name: t("sidebar.billing"),
      href: `/shops/${shop.id}/settings/billing`,
      icon: <FileTextIcon className="w-4 h-4" />,
    });
  }

  return (
    <>
      {navItems.map((navItem) => (
        <Fragment key={navItem.name}>
          <NavigationSectionItem
            to={navItem.href}
            label={navItem.name}
            icon={navItem.icon}
            badge={navItem.badge}
            locked={navItem.locked}
          />
          {navItem.href.endsWith("/settings/customer-portal") && (
            <NotificationsNavItem shopId={shop.id} />
          )}
        </Fragment>
      ))}
    </>
  );
}
